import React from "react";
import { motion } from "framer-motion";
import { Image } from "primereact/image";
import {
  FaUser,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
  FaCity,
  FaFileInvoiceDollar,
  FaTruck,
  FaWarehouse,
  FaBoxOpen,
  FaMoneyBillWave,
  FaCreditCard,
} from "react-icons/fa";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const STATUS_STYLES = {
  pending: { bg: "#FEF3C7", text: "#92400E", dot: "#D97706" },
  to_pack: { bg: "#DBEAFE", text: "#1E40AF", dot: "#2563EB" },
  arrange_shipment: { bg: "#EDE9FE", text: "#5B21B6", dot: "#7C3AED" },
  hand_over: { bg: "#FFEDD5", text: "#9A3412", dot: "#EA580C" },
  shipping: { bg: "#D1FAE5", text: "#065F46", dot: "#059669" },
  delivered: { bg: "#DCFCE7", text: "#166534", dot: "#16A34A" },
  cancelled: { bg: "#FEE2E2", text: "#991B1B", dot: "#DC2626" },
};

const PAYMENT_STYLES = {
  paid: { bg: "#DCFCE7", text: "#166534" },
  pending: { bg: "#FEF3C7", text: "#92400E" },
  failed: { bg: "#FEE2E2", text: "#991B1B" },
  refunded: { bg: "#E0E7FF", text: "#3730A3" },
};

function StatusBadge({ value, styles, fallback }) {
  const key = (value || "").toLowerCase();
  const s = styles[key] || fallback;
  const label = (value || "N/A").replace(/_/g, " ");
  return (
    <span
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold capitalize"
      style={{ background: s.bg, color: s.text }}
    >
      {s.dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />}
      {label}
    </span>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center shrink-0 text-gray-500">
        <Icon size={14} />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</div>
        <div className="text-sm text-gray-800 break-words">{value}</div>
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, accent, children }) {
  return (
    <motion.div
      variants={cardVariants}
      className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden h-full"
    >
      <div
        className="px-5 py-3.5 flex items-center gap-2.5 border-b border-gray-100"
        style={{ background: accent.bg }}
      >
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0"
          style={{ background: accent.icon }}
        >
          <Icon size={14} />
        </div>
        <h3 className="text-sm font-bold tracking-wide uppercase" style={{ color: accent.text }}>
          {title}
        </h3>
      </div>
      <div className="p-5">{children}</div>
    </motion.div>
  );
}

export default function OrderReviewSummary({ order }) {
  if (!order) return null;

  const customerFullName = [order.customer_name, order.customer_last_name].filter(Boolean).join(" ") || "Customer";
  const orderDate = order.created_at
    ? new Date(order.created_at).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

  const products = order.products || [];

  const shippingCity = order.shipping_city || null;
  const billingCity = order.billing_city || null;
  const sameCity = shippingCity && billingCity && shippingCity === billingCity;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="show"
      className="mb-6"
    >
      {/* Order header strip */}
      <motion.div
        variants={cardVariants}
        className="bg-gradient-to-r from-gray-900 to-gray-700 rounded-2xl px-6 py-5 mb-4 flex flex-wrap items-center justify-between gap-4 text-white shadow-md"
      >
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-300 font-medium">Order</div>
          <div className="text-2xl font-extrabold tracking-tight">
            #{order.order_number || order.id}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge value={order.status} styles={STATUS_STYLES} fallback={{ bg: "#E5E7EB", text: "#374151" }} />
          <StatusBadge value={order.payment_status} styles={PAYMENT_STYLES} fallback={{ bg: "#E5E7EB", text: "#374151" }} />
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Customer Details */}
        <SectionCard
          title="Customer Details"
          icon={FaUser}
          accent={{ bg: "#EFF6FF", icon: "#2563EB", text: "#1E40AF" }}
        >
          <InfoRow icon={FaUser} label="Name" value={customerFullName} />
          <InfoRow icon={FaEnvelope} label="Email" value={order.customer_email} />
          <InfoRow icon={FaPhoneAlt} label="Contact" value={order.contact} />
          {sameCity ? (
            <InfoRow icon={FaCity} label="City" value={shippingCity} />
          ) : (
            <>
              <InfoRow icon={FaCity} label="Shipping City" value={shippingCity} />
              <InfoRow icon={FaCity} label="Billing City" value={billingCity} />
            </>
          )}
          <InfoRow icon={FaMapMarkerAlt} label="Shipping Address" value={order.shipping_address} />
          <InfoRow icon={FaMapMarkerAlt} label="Billing Address" value={order.billing_address} />
        </SectionCard>

        {/* Order Details */}
        <SectionCard
          title="Order Details"
          icon={FaFileInvoiceDollar}
          accent={{ bg: "#F5F3FF", icon: "#7C3AED", text: "#5B21B6" }}
        >
          <InfoRow icon={FaFileInvoiceDollar} label="Order Date" value={orderDate} />
          <InfoRow icon={FaCreditCard} label="Payment Method" value={order.payment_method} />
          <InfoRow icon={FaWarehouse} label="Warehouse" value={order.warehouse_name} />
          <InfoRow icon={FaMapMarkerAlt} label="Warehouse Address" value={[order.warehouse_address, order.warehouse_city].filter(Boolean).join(", ")} />
          <InfoRow icon={FaTruck} label="Courier" value={order.courier_company} />
          <InfoRow icon={FaTruck} label="Tracking ID" value={order.tracking_id} />
        </SectionCard>

        {/* Payment Summary */}
        <SectionCard
          title="Payment Summary"
          icon={FaMoneyBillWave}
          accent={{ bg: "#ECFDF5", icon: "#059669", text: "#065F46" }}
        >
          <div className="space-y-2.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Subtotal (Sales)</span>
              <span className="font-medium text-gray-800">
                RS {Number(order.total_sales || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Discount</span>
              <span className="font-medium text-red-500">
                - RS {Number(order.total_discount || 0).toLocaleString("en-IN")}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Shipping</span>
              <span className="font-medium text-gray-800">
                RS {Number(order.shipping_amount || 0).toLocaleString("en-IN")}
              </span>
            </div>
            {Number(order.vat_amount || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">VAT ({order.vat_per || 0}%)</span>
                <span className="font-medium text-gray-800">
                  RS {Number(order.vat_amount || 0).toLocaleString("en-IN")}
                </span>
              </div>
            )}
            <div className="border-t border-gray-100 pt-2.5 flex justify-between items-center">
              <span className="text-sm font-bold text-gray-800">Total Amount</span>
              <span className="text-lg font-extrabold" style={{ color: "#059669" }}>
                RS {Number(order.total_amount || 0).toLocaleString("en-IN")}
              </span>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Product Details */}
      <motion.div
        variants={cardVariants}
        className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
      >
        <div className="px-5 py-3.5 flex items-center gap-2.5 border-b border-gray-100" style={{ background: "#FFF7ED" }}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: "#EA580C" }}>
            <FaBoxOpen size={14} />
          </div>
          <h3 className="text-sm font-bold tracking-wide uppercase" style={{ color: "#9A3412" }}>
            Product Details ({products.length})
          </h3>
        </div>

        <div className="divide-y divide-gray-50">
          {products.length === 0 && (
            <div className="p-6 text-center text-sm text-gray-400">No products found for this order.</div>
          )}
          {products.map((p, idx) => (
            <motion.div
              key={p.order_product_id || p.id || idx}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.05 * idx, duration: 0.3 }}
              className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/80 transition-colors duration-200"
            >
              <div className="w-16 h-16 rounded-xl bg-gray-100 overflow-hidden shrink-0 flex items-center justify-center">
                {p.image_path ? (
                  <Image
                    src={p.image_path}
                    alt={p.title}
                    width="64"
                    height="64"
                    imageStyle={{ objectFit: "cover", cursor: "zoom-in" }}
                    preview
                  />
                ) : (
                  <FaBoxOpen className="text-gray-300" size={20} />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-800 truncate">{p.title || "Product"}</div>
                {p.properties && p.properties.length > 0 && (
                  <div className="text-xs text-gray-400 mt-0.5">
                    {p.properties.map((prop) => `${prop.name}: ${prop.value}`).join(" · ")}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="inline-block px-2 py-0.5 rounded-md bg-gray-100 text-xs font-medium text-gray-600">
                    Qty: {p.quantity}
                  </span>
                  {Number(p.discount || 0) > 0 && (
                    <span className="inline-block px-2 py-0.5 rounded-md bg-red-50 text-xs font-medium text-red-500">
                      -RS {Number(p.discount).toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
              </div>

              <div className="text-right shrink-0">
                <div className="text-xs text-gray-400">RS {Number(p.salePrice || 0).toLocaleString("en-IN")} each</div>
                <div className="text-base font-bold text-gray-800">
                  RS {Number(p.total || 0).toLocaleString("en-IN")}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
